import { SrInsightType as InsightType } from '@plato/data/starrocks/types';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { plainTextToLexicalSync } from '@plato/ui-legacy/lib/rich-text/plain-text-to-lexical';
import {
    Collapsible,
    CollapsibleHeader,
    CollapsibleHeading,
    CollapsiblePanel,
    CollapsibleSubtitle,
    CollapsibleTitle,
    CollapsibleTrigger,
} from '@plato/ui/containers/collapsible';
import { Join } from '@plato/ui/containers/join';
import { Button } from '@plato/ui/primitives/button';
import { Typography } from '@plato/ui/primitives/typography';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmLinkModelType } from '../../../server/dto/crm-task-dto.ts';
import type { CrmTaskIntent } from '../../../server/dto/crm-task-input-dto.ts';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import {
    useInsightsWithAnalytics,
    type SendAnalyticsEventHandler,
} from '../../insights-common/hooks/use-insights-with-analytics.ts';
import { getInsightName } from '../../insights-common/lib/insight-name.ts';
import type { TaskBulkFormValues } from '../../tasks/components/task-bulk-form-dialog.tsx';
import type { TaskListItem } from '../log-interaction-dialog-context.ts';
import { useLogInteractionDialog } from '../log-interaction-dialog-provider.tsx';
import { LogInteractionInsightCard } from './log-interaction-insight-card.tsx';
import { TaskBulkDialog } from './log-interaction-task-bulk-dialog.tsx';
import { LogInteractionTaskCard } from './log-interaction-task-card.tsx';

const maxVisiblePerGroup = 3;
const insightLinkType: CrmLinkModelType = 'ai_insights';

function useTasksLinkedToInsight(insightId: string): TaskListItem[] {
    return useLogInteractionDialog().stagedTasks.filter((item) =>
        item.values.links.some((link) => link.modelType === insightLinkType && link.modelId === insightId),
    );
}

function InsightRow({
    insightData,
    onAnalyticsEvent,
}: {
    insightData: InsightListItem;
    onAnalyticsEvent: SendAnalyticsEventHandler;
}): ReactNode {
    const { t } = useTranslation();
    const { customerId, customerName, addTasks } = useLogInteractionDialog();
    const tasks = useTasksLinkedToInsight(insightData.insight.id);

    const { insight, details } = insightData;
    const name = getInsightName(t, insight);
    const category = details.category.title ?? insight.actionable_entity_id;

    const openCreateTask = (description: string, taskIntent?: CrmTaskIntent): void => {
        const initialValues: Partial<TaskBulkFormValues> = {
            description: description ? (plainTextToLexicalSync(description) ?? undefined) : undefined,
            taskIntent,
            links: [
                {
                    modelType: SearchEntity.Customer,
                    modelId: customerId,
                    title: customerName ?? null,
                },
                {
                    modelType: insightLinkType,
                    modelId: insight.id,
                    title: name,
                },
            ],
        };

        void DialogStateManager.show(TaskBulkDialog, {
            initialValues,
            onSubmit: (newTasks) => {
                addTasks(newTasks, 'ai_insights');
            },
        });
    };

    return (
        <Join orientation="vertical">
            <LogInteractionInsightCard
                insightData={insightData}
                onAnalyticsEvent={onAnalyticsEvent}
                onCreateTask={() => {
                    openCreateTask(`${name}: ${category}`);
                }}
            />
            {tasks.map((task) => (
                <LogInteractionTaskCard key={task.key} item={task} />
            ))}
        </Join>
    );
}

export function LogInteractionInsightList(): ReactNode {
    const { t } = useTranslation('notes');
    const { customerId } = useLogInteractionDialog();

    const { insights, isLoading, sendAnalytics } = useInsightsWithAnalytics({ customerId });

    const [showAllRisks, setShowAllRisks] = useState(false);
    const [showAllOpportunities, setShowAllOpportunities] = useState(false);

    const isEmpty = insights.length === 0;

    const risks = insights.filter((i) => i.insight.insight_type === InsightType.CHURN_WARNING);
    const opportunities = insights.filter((i) => i.insight.insight_type !== InsightType.CHURN_WARNING);

    const summaryParts = [
        { count: risks.length, label: t('risks', { ns: 'insights' }) },
        { count: opportunities.length, label: t('opportunities', { ns: 'insights' }) },
    ]
        .filter((part) => part.count > 0)
        .map((part) => `${part.count} ${part.label}`);

    return (
        <Collapsible>
            <CollapsibleHeader>
                <CollapsibleHeading>
                    <CollapsibleTitle>{t('notes__ai_insights_section_title')}</CollapsibleTitle>
                    {summaryParts.length > 0 && (
                        <CollapsibleSubtitle>{summaryParts.join(' · ')}</CollapsibleSubtitle>
                    )}
                </CollapsibleHeading>
                <CollapsibleTrigger />
            </CollapsibleHeader>
            <CollapsiblePanel>
                {isEmpty ? (
                    <p className="text-body text-content-weak">
                        {isLoading
                            ? t('notes__ai_insights_section_loading')
                            : t('notes__ai_insights_section_empty')}
                    </p>
                ) : (
                    <div className="flex flex-col gap-default">
                        {risks.length > 0 && (
                            <div className="flex flex-col gap-cozy">
                                <Typography variant="subtle-heading">
                                    {t('risks', { ns: 'insights' })}
                                </Typography>
                                {(showAllRisks ? risks : risks.slice(0, maxVisiblePerGroup)).map(
                                    (insightData) => (
                                        <InsightRow
                                            key={insightData.insight.id}
                                            insightData={insightData}
                                            onAnalyticsEvent={sendAnalytics}
                                        />
                                    ),
                                )}
                                {!showAllRisks && risks.length > maxVisiblePerGroup && (
                                    <Button
                                        variant="secondary"
                                        className="self-start"
                                        onClick={() => {
                                            setShowAllRisks(true);
                                        }}
                                    >
                                        {t('notes__ai_insights_show_more', {
                                            count: risks.length - maxVisiblePerGroup,
                                        })}
                                    </Button>
                                )}
                            </div>
                        )}
                        {opportunities.length > 0 && (
                            <div className="flex flex-col gap-cozy">
                                <Typography variant="subtle-heading">
                                    {t('opportunities', { ns: 'insights' })}
                                </Typography>
                                {(showAllOpportunities
                                    ? opportunities
                                    : opportunities.slice(0, maxVisiblePerGroup)
                                ).map((insightData) => (
                                    <InsightRow
                                        key={insightData.insight.id}
                                        insightData={insightData}
                                        onAnalyticsEvent={sendAnalytics}
                                    />
                                ))}
                                {!showAllOpportunities && opportunities.length > maxVisiblePerGroup && (
                                    <Button
                                        variant="secondary"
                                        className="self-start"
                                        onClick={() => {
                                            setShowAllOpportunities(true);
                                        }}
                                    >
                                        {t('notes__ai_insights_show_more', {
                                            count: opportunities.length - maxVisiblePerGroup,
                                        })}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </CollapsiblePanel>
        </Collapsible>
    );
}
